import { CategoryFilterComponent } from './../category-filter/category-filter.component'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { ContextService } from '../../services/context.service'
import { Delivery, Vertical } from '../../models/Vertical'
import { BaseFilter } from '../../models/BaseFilter'
import { Subscription } from 'rxjs'
import { Location } from '@angular/common'
import { animate, state, style, transition, trigger } from '@angular/animations'
import { TranslateService } from '@ngx-translate/core'
import { toDate, formatDate } from 'app/utils/date'
import { merge } from 'rxjs'
import { Colors } from '../config/constants'
import { injectDeliveriesQuery } from '../../mobility/ooh/model/deliveries.model'

@Component({
  standalone: false,
  selector: 'app-filter-bar',
  templateUrl: './filter-bar.component.html',
  styleUrls: ['./filter-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // TODO: move to utils/animations.ts
  animations: [
    trigger('openCloseFilterBar', [
      state('close', style({ width: '0' })),
      state('open', style({ width: '240px' })),
      transition('close <=> open', animate(`500ms ease-out`)),
    ]),
    trigger('openCloseToggleBtn', [
      state(
        'close',
        style({
          transform: 'translateX(calc(-100% - 24px)) rotateY(0deg)',
          backgroundColor: Colors.colorWhite,
          boxShadow: '0 0 6px 0 color.adjust($colorBlack, $alpha: -0.75)',
        }),
      ),
      state(
        'open',
        style({
          transform: 'translateX(-50%) rotateY(180deg)',
          backgroundColor: Colors.colorN100,
          boxShadow: '0 0 6px 0 color.adjust($colorBlack, $alpha: -1)',
        }),
      ),
      transition('close <=> open', animate(`500ms ease-out`)),
    ]),
  ],
})
export class FilterBarComponent implements OnInit, OnDestroy {
  protected _filters: any[] = []
  protected _target: any
  protected _deliveries: Delivery[] = []
  deliveriesLabel = 'Deliveries'

  compareSelectorLabel = 'Temporal comparison'
  deliveriesQuery = injectDeliveriesQuery()

  @HostBinding('class.shown') shown = true
  @ViewChild('targetCmp', { read: CategoryFilterComponent, static: false })
  targetCmp: CategoryFilterComponent

  subscriptions: Subscription = new Subscription()
  brand = 'placeholder'

  openCurrentDelivery = false
  openCompareDeliveries = false
  compareDelivery = null
  _compareDeliveries: Delivery[] = []
  currentDeliveryDate = null

  get keyIndicator() {
    return this.context.keyIndicator.value
  }

  get isVisitsAvailable() {
    return this.context.isVisitsAvailable()
  }

  @Input() showCurrentDeliveries = true

  private _showCompareDeliveries = this.location.path().indexOf('map') !== -1
  @Input() get showCompareDeliveries() {
    return this._showCompareDeliveries
  }

  set showCompareDeliveries(s) {
    if (!s) {
      this.changeCompareDelivery(null, false)
    }
    this._showCompareDeliveries = s
  }

  get filters(): any[] {
    return this._filters
  }

  get target(): any {
    return this._target
  }

  get deliveries() {
    return this._deliveries
      .filter((delivery: Delivery) => delivery.start)
      .map((delivery: Delivery) => {
        if (delivery.granularity === 'week') {
          return {
            label: this.createDeliveryLabel(delivery),
            value: delivery.id,
          }
        } else {
          const d = toDate(delivery.start)
          const month = formatDate(d, { month: 'long' })
          const year = d.getFullYear()
          return {
            label: delivery.name || `${month} ${year}`,
            value: delivery.id,
          }
        }
      })
  }

  get currentDelivery() {
    const delivery = this.context.vertical.delivery
    if (delivery.start) {
      if (delivery.granularity === 'week') {
        this.currentDeliveryDate = this.createDeliveryLabel(delivery)
      } else {
        const d = toDate(delivery.start)
        const month = formatDate(d, { month: 'long' })
        const year = d.getFullYear()
        this.currentDeliveryDate = `${month} ${year}`
      }
    }

    return this.context.vertical.delivery
  }

  get compareDeliveries() {
    return this._compareDeliveries
      .filter((delivery: Delivery) => delivery.start)
      .map((delivery: Delivery) => {
        if (delivery.granularity === 'week') {
          return {
            label: this.createDeliveryLabel(delivery),
            value: delivery.id,
          }
        } else {
          const d = toDate(delivery.start)
          const month = formatDate(d, { month: 'long' })
          const year = d.getFullYear()
          return {
            label: delivery.name || `${month} ${year}`,
            value: delivery.id,
          }
        }
      })
  }

  @Output() shownToggle = new EventEmitter<boolean>()
  @Output() finishTogggleAnimation = new EventEmitter<boolean>()
  @Output() deliveryToCompare = new EventEmitter<any>()

  get keyIndicatorOptions() {
    return this.context.keyIndicatorOptions
  }

  get isEntirePopulationActive() {
    return this.context.isEntirePopulationActive()
  }

  constructor(
    protected context: ContextService,
    protected changes: ChangeDetectorRef,
    protected location: Location,
    protected translateService: TranslateService,
  ) {
    effect(() => {
      const rows = this.deliveriesQuery.data()
      if (!rows?.length) return

      // Carry over parsed config from the metadata delivery so
      // filters, target and tagGroups are preserved on new deliveries
      const baseDelivery = this.context.vertical?.delivery
      const baseConfig = baseDelivery?.config
      const baseVariablesKey = baseDelivery?.variablesKey

      const deliveries = rows.map((row) => {
        const d = new Delivery({
          id: row.id,
          name: row.name,
          start: row.start,
          finish: row.finish,
          granularity: row.granularity,
          type: row.type,
        })
        if (baseConfig) {
          d.config = baseConfig
        }
        if (baseVariablesKey) {
          d.variablesKey = baseVariablesKey
        }
        return d
      })

      // Store deliveries on the vertical so initFilters picks them up
      const territory = this.context.city?.name
      if (territory && this.context.vertical) {
        this.context.vertical.deliveries[territory] = deliveries
      }

      this.context.setDelivery(deliveries[0])
    })
  }

  ngOnInit() {
    this.subscriptions.add(
      merge(
        this.context.$currentVertical,
        this.context.deliveriesChanges,
        this.context.filterChanges,
      ).subscribe(() => {
        this.initFilters(this.context.vertical)
      }),
    )
    this.subscriptions.add(
      this.context.poiChanges.subscribe(() => {
        if (this.hasMobilityFilters()) {
          this.initFilters(this.context.vertical)
        }
      }),
    )
    this.shown = this.context.filterBarOpen
  }

  protected hasMobilityFilters() {
    return (
      this.context.vertical &&
      this.context.vertical.delivery.filtersMobility.length > 0
    )
  }

  createDeliveryLabel(delivery) {
    const s = toDate(delivery.start)
    const f = toDate(delivery.finish)
    const year = s.getFullYear()
    const from = formatDate(s, { day: 'numeric', month: 'short' })
    const to = formatDate(f, { day: 'numeric', month: 'short' })
    return `${year} / ${from} - ${to}`
  }

  initFilters(vertical: Vertical) {
    const useMobility = this.context.poi && this.hasMobilityFilters()
    const contextFilters = useMobility
      ? vertical.delivery.filtersMobility
      : vertical.delivery.filters
    this._filters = []
    for (const name in contextFilters) {
      if (contextFilters[name]) {
        this._filters.push(contextFilters[name])
      }
    }
    this._target = vertical.delivery.target
    this._deliveries = vertical.deliveries[this.context.city.name] || []
    this._compareDeliveries = this._deliveries.filter(
      (delivery) => delivery !== this.currentDelivery,
    )
    this.brand = this.context.organization.name
    this.changes.markForCheck()
  }

  ngOnDestroy() {
    this.changes.detach()
    this.subscriptions.unsubscribe()
  }

  togglePanel() {
    this.shown = !this.shown
    this.shownToggle.emit(this.shown)
    this.context.toggleFilterBar(this.shown)
    this.changes.markForCheck()
  }

  onFilterChange(filter: BaseFilter, element) {
    const { filters, filtersMobility } = this.context.vertical.delivery
    const foundAbsolute = filters.find((f) => f.id === filter.id)
    const foundMobility = filtersMobility.find((f) => f.id === filter.id)
    if (foundAbsolute) {
      foundAbsolute.value = element.value
    }
    if (foundMobility) {
      foundMobility.value = element.value
    }
    this.context.setFilter(filter)
    this.changes.markForCheck()
  }

  onFinishToggle(ev) {
    this.finishTogggleAnimation.emit()
  }

  changeDelivery(newDelivery) {
    const delivery = this._deliveries.find(
      (item) => Number(item.id) === Number(newDelivery.value),
    )
    delivery.variablesKey = this.currentDelivery.variablesKey
    this.openCurrentDelivery = false
    this.context.setDelivery(delivery)
    this._compareDeliveries = this._deliveries.filter((del) => del !== delivery)
    this.changes.markForCheck()
  }

  changeCompareDelivery(newDelivery, emit = true) {
    const delivery = newDelivery
      ? this._deliveries.find(
          (item) => Number(item.id) === Number(newDelivery.value),
        )
      : null
    this.openCompareDeliveries = false
    this.compareDelivery = delivery
    if (emit) {
      this.deliveryToCompare.emit(delivery)
    }
  }

  isDeliverySelected(delivery, compareMode = false) {
    const current = compareMode ? this.compareDelivery : this.currentDelivery
    return current && current.id === delivery.value
  }

  toggleDeliveriesMenu() {
    this.openCurrentDelivery = !this.openCurrentDelivery
  }

  toggleCompareDeliveriesMenu() {
    this.openCompareDeliveries = !this.openCompareDeliveries
  }

  onKeyIndicatorChange(value) {
    if (!this.targetCmp.subfiltersDisabled) {
      this.targetCmp.toggleSubfilters()
    }
    this.context.setKeyIndicator(value)
  }
}
